import type { Account } from '@deskworks/shared-types';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

export function CreateWorkspaceDialog({
  accounts,
  defaultAccountId,
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  accounts: Account[];
  defaultAccountId?: string;
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const setOpen = onOpenChange ?? setInnerOpen;
  const [accountId, setAccountId] = useState(defaultAccountId ?? accounts[0]?.id ?? '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const create = useApiMutation(
    () => api.accounts.createWorkspace(accountId, { name: name.trim(), description: description.trim() || undefined }),
    {
      success: t('Workspace created'),
      invalidate: [qk.accountWorkspaces(accountId)],
      onSuccess: (ws) => {
        setOpen(false);
        setName('');
        setDescription('');
        void navigate({ to: '/w/$workspaceId', params: { workspaceId: ws.id } });
      },
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !accountId) return;
    create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('New workspace')}</DialogTitle>
            <DialogDescription>{t('A Workspace groups desks, agents and connections for one area of your company.')}</DialogDescription>
          </DialogHeader>
          {accounts.length > 1 && (
            <Field label={t('Account')}>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Choose account')} />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label={t('Name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Customer service')} autoFocus required />
          </Field>
          <Field label={t('Description')} optional>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
              {t('Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
